import { motion } from "motion/react";
import { useAuth } from "../../auth/AuthContext";
import { Card } from "../../ui/Card";
import { Button } from "../../ui/Button";
import { useDemoMode } from "../../lib/demo/demoMode";

const USERS_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
  </svg>
);
const BUILDING_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 21h18" />
    <path d="M5 21V7l8-4v18" />
    <path d="M19 21V11l-6-4" />
  </svg>
);
const CARD_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="6" width="20" height="14" rx="2" />
    <line x1="2" y1="11" x2="22" y2="11" />
  </svg>
);
const CLOCK_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

/**
 * Dashboard · home post-login.
 * - Mode normal : salutation + 4 KPI + checklist de mise en route (les vraies
 *   données métier arrivent à la Vague 1).
 * - Mode démo : aperçu riche d'une agence fictive (KPI, planning du jour,
 *   conformité, activité). 100% présentationnel, aucune requête réseau.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const demoActive = useDemoMode();
  const prenom = user?.prenom ?? "vous";

  if (demoActive) {
    return <DemoDashboard prenom={prenom} />;
  }

  return (
    <div className="space-y-8">
      {/* Hello */}
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-3xl font-bold tracking-tight text-navy-800 sm:text-4xl">
          Bonjour, {prenom}.
        </h1>
        <p className="mt-2 text-navy-600">
          Voici un aperçu de votre agence aujourd'hui.
        </p>
      </motion.header>

      {/* KPI cards */}
      <section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {KPIS.map((kpi, i) => (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
            >
              <Card>
                <div className="flex items-start justify-between">
                  <span
                    aria-hidden="true"
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${kpi.bg} ${kpi.color}`}
                  >
                    {kpi.icon}
                  </span>
                  {kpi.trend ? (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                        kpi.trend.positive
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {kpi.trend.value}
                    </span>
                  ) : null}
                </div>
                <p className="mt-4 text-sm text-navy-500">{kpi.label}</p>
                <p className="mt-1 text-3xl font-black tracking-tight text-navy-800">
                  {kpi.value}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Setup checklist + actions */}
      <section className="grid gap-5 lg:grid-cols-3">
        <motion.div
          className="lg:col-span-2"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-accent-500">
                  Configurez Victor
                </p>
                <h2 className="mt-1 text-xl font-bold text-navy-800">
                  Votre agence en 5 étapes
                </h2>
                <p className="mt-1 text-sm text-navy-600">
                  Une checklist pour partir vite. Vous pouvez y revenir à tout
                  moment depuis les paramètres.
                </p>
              </div>
            </div>
            <ul className="mt-6 space-y-3">
              {CHECKLIST.map((item) => (
                <li
                  key={item.label}
                  className="flex items-start gap-3 rounded-xl bg-navy-50/60 p-3 ring-1 ring-navy-100"
                >
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-black ${
                      item.done
                        ? "bg-emerald-500 text-white"
                        : "bg-white text-navy-400 ring-1 ring-navy-200"
                    }`}
                  >
                    {item.done ? "✓" : ""}
                  </span>
                  <div className="flex-1">
                    <p
                      className={`text-sm font-semibold ${
                        item.done ? "text-navy-500 line-through" : "text-navy-800"
                      }`}
                    >
                      {item.label}
                    </p>
                    <p className="mt-0.5 text-xs text-navy-500">{item.desc}</p>
                  </div>
                  {!item.done ? (
                    <Button href={item.href} variant="secondary" size="sm">
                      Configurer
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <p className="text-xs font-bold uppercase tracking-wider text-accent-500">
              Besoin d'un coup de main ?
            </p>
            <h2 className="mt-1 text-xl font-bold text-navy-800">
              On reste joignables.
            </h2>
            <p className="mt-2 text-sm text-navy-600">
              Une question, une difficulté, une suggestion ? Écrivez-nous
              directement.
            </p>
            <div className="mt-5 space-y-2">
              <Button
                href="mailto:hello@hubblot.fr"
                external
                variant="primary"
                fullWidth
                size="sm"
              >
                Contacter l'équipe Victor
              </Button>
              <Button
                href="https://myvictor.fr"
                external
                variant="secondary"
                fullWidth
                size="sm"
              >
                Centre d'aide
              </Button>
            </div>
          </Card>
        </motion.div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Mode démo : aperçu d'une agence fictive « Sécurité Lumen »          */
/* ------------------------------------------------------------------ */

function DemoDashboard({ prenom }: { prenom: string }) {
  return (
    <div className="space-y-8">
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-3xl font-bold tracking-tight text-navy-800 sm:text-4xl">
          Bonjour, {prenom}.
        </h1>
        <p className="mt-2 text-navy-600">
          Aperçu de <strong>Sécurité Lumen</strong> aujourd'hui — mercredi 17 juin.
        </p>
      </motion.header>

      {/* KPI */}
      <section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {DEMO_KPIS.map((kpi, i) => (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
            >
              <Card>
                <div className="flex items-start justify-between">
                  <span
                    aria-hidden="true"
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${kpi.bg} ${kpi.color}`}
                  >
                    {kpi.icon}
                  </span>
                  {kpi.trend ? (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                        kpi.trend.positive
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {kpi.trend.value}
                    </span>
                  ) : null}
                </div>
                <p className="mt-4 text-sm text-navy-500">{kpi.label}</p>
                <p className="mt-1 text-3xl font-black tracking-tight text-navy-800">
                  {kpi.value}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="grid gap-5 lg:grid-cols-3">
        {/* Planning du jour */}
        <motion.div
          className="lg:col-span-2"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-accent-500">
                  En ce moment
                </p>
                <h2 className="mt-1 text-xl font-bold text-navy-800">
                  Planning du jour
                </h2>
              </div>
              <Button href="/planning" variant="secondary" size="sm">
                Ouvrir le planning
              </Button>
            </div>
            <ul className="mt-5 divide-y divide-navy-100">
              {DEMO_PLANNING.map((v) => (
                <li key={v.agent + v.site} className="flex items-center gap-3 py-3">
                  <span
                    className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${v.dotColor}`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-navy-800">
                      {v.agent}
                    </p>
                    <p className="truncate text-xs text-navy-500">{v.site}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-navy-700">{v.horaire}</p>
                    <p className="text-xs text-navy-400">{v.poste}</p>
                  </div>
                  <span
                    className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${v.badgeCls}`}
                  >
                    {v.statut}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </motion.div>

        {/* Conformité */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <p className="text-xs font-bold uppercase tracking-wider text-amber-600">
              À surveiller
            </p>
            <h2 className="mt-1 text-xl font-bold text-navy-800">Conformité</h2>
            <ul className="mt-4 space-y-3">
              {DEMO_CONFORMITE.map((c) => (
                <li
                  key={c.titre}
                  className="rounded-xl bg-amber-50/70 p-3 ring-1 ring-amber-100"
                >
                  <p className="text-sm font-semibold text-navy-800">{c.titre}</p>
                  <p className="mt-0.5 text-xs text-navy-600">{c.detail}</p>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                    {c.echeance}
                  </p>
                </li>
              ))}
            </ul>
            <Button
              href="/agents"
              variant="secondary"
              size="sm"
              fullWidth
              className="mt-4"
            >
              Voir les qualifications
            </Button>
          </Card>
        </motion.div>
      </section>

      {/* Activité récente */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card>
          <h2 className="text-xl font-bold text-navy-800">Activité récente</h2>
          <ul className="mt-4 space-y-3">
            {DEMO_ACTIVITE.map((a, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-2 w-2 shrink-0 rounded-full bg-accent-400" aria-hidden="true" />
                <p className="text-sm text-navy-700">
                  {a.texte}{" "}
                  <span className="text-navy-400">· {a.quand}</span>
                </p>
              </li>
            ))}
          </ul>
        </Card>
      </motion.div>
    </div>
  );
}

const DEMO_KPIS: Kpi[] = [
  {
    label: "Agents gérés",
    value: "38",
    bg: "bg-accent-50",
    color: "text-accent-500",
    trend: { positive: true, value: "+3 ce mois" },
    icon: USERS_ICON,
  },
  {
    label: "Sites actifs",
    value: "5",
    bg: "bg-navy-50",
    color: "text-navy-700",
    icon: BUILDING_ICON,
  },
  {
    label: "Cartes pro à renouveler",
    value: "2",
    bg: "bg-amber-50",
    color: "text-amber-600",
    trend: { positive: false, value: "< 60 j" },
    icon: CARD_ICON,
  },
  {
    label: "Heures planifiées (7 j)",
    value: "1 248 h",
    bg: "bg-emerald-50",
    color: "text-emerald-600",
    trend: { positive: true, value: "98% couvert" },
    icon: CLOCK_ICON,
  },
];

const DEMO_PLANNING = [
  {
    agent: "Karim Benali",
    site: "Centre commercial Euralille",
    horaire: "08:00 – 20:00",
    poste: "Agent de sécurité",
    statut: "En poste",
    dotColor: "bg-emerald-500",
    badgeCls: "bg-emerald-50 text-emerald-700",
  },
  {
    agent: "Sophie Marchand",
    site: "Siège régional — Groupe Verlaine",
    horaire: "07:00 – 19:00",
    poste: "SSIAP 1",
    statut: "En poste",
    dotColor: "bg-emerald-500",
    badgeCls: "bg-emerald-50 text-emerald-700",
  },
  {
    agent: "Damien Leroy",
    site: "Foire commerciale de Lille",
    horaire: "14:00 – 22:00",
    poste: "Agent événementiel",
    statut: "À venir",
    dotColor: "bg-accent-400",
    badgeCls: "bg-accent-50 text-accent-700",
  },
  {
    agent: "Poste à pourvoir",
    site: "Plateau logistique — Lesquin",
    horaire: "20:00 – 06:00",
    poste: "Rondier nuit",
    statut: "Non couvert",
    dotColor: "bg-red-500",
    badgeCls: "bg-red-50 text-red-700",
  },
];

const DEMO_CONFORMITE = [
  {
    titre: "Carte professionnelle — Damien Leroy",
    detail: "CNAPS n° 059-2021-…  expire bientôt.",
    echeance: "Dans 23 jours",
  },
  {
    titre: "Recyclage SSIAP 1 — Sophie Marchand",
    detail: "Maintien des acquis à programmer.",
    echeance: "Dans 41 jours",
  },
];

const DEMO_ACTIVITE = [
  { texte: "Pointage validé — Karim Benali (Euralille).", quand: "il y a 12 min" },
  { texte: "Main courante : ronde 02h00 RAS — Lesquin.", quand: "il y a 1 h" },
  { texte: "Facture F-2026-0142 envoyée — Groupe Verlaine.", quand: "il y a 3 h" },
  { texte: "Nouvel agent ajouté — Inès Fontaine.", quand: "hier" },
];

type Kpi = {
  label: string;
  value: string;
  bg: string;
  color: string;
  trend?: { positive: boolean; value: string };
  icon: React.ReactNode;
};

const KPIS: Kpi[] = [
  {
    label: "Agents gérés",
    value: "-",
    bg: "bg-accent-50",
    color: "text-accent-500",
    icon: USERS_ICON,
  },
  {
    label: "Établissements",
    value: "-",
    bg: "bg-navy-50",
    color: "text-navy-700",
    icon: BUILDING_ICON,
  },
  {
    label: "Cartes pro à renouveler",
    value: "0",
    bg: "bg-amber-50",
    color: "text-amber-600",
    icon: CARD_ICON,
  },
  {
    label: "Heures sur 30 jours",
    value: "-",
    bg: "bg-emerald-50",
    color: "text-emerald-600",
    icon: CLOCK_ICON,
  },
];

const CHECKLIST = [
  {
    label: "Personnaliser votre branding",
    desc: "Logo, couleurs et nom affiché de votre agence.",
    href: "/parametres",
    done: false,
  },
  {
    label: "Créer vos établissements",
    desc: "Vos sièges, agences régionales, ou points de gestion.",
    href: "/etablissements",
    done: false,
  },
  {
    label: "Inviter vos premiers utilisateurs",
    desc: "Planificateurs, RH et gérants. Ils reçoivent un email d'invitation.",
    href: "/utilisateurs",
    done: false,
  },
  {
    label: "Activer la double authentification",
    desc: "Recommandé pour les comptes gérants.",
    href: "/mon-compte",
    done: false,
  },
  {
    label: "Préparer vos données agents",
    desc: "Import depuis Excel disponible · gain de temps significatif.",
    href: "/parametres",
    done: false,
  },
] as const;
